import React, { useState, useRef, useEffect } from 'react';

export default function PopupManager() {
  const [popupData, setPopupData] = useState({
    pu_no: '',
    pu_file1_s: '',
    pu_file1_o: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // 컴포넌트 마운트 시 현재 팝업 정보 로드
  useEffect(() => {
    loadCurrentPopup();
  }, []);

  // 현재 팝업 정보 불러오기
  const loadCurrentPopup = async () => {
    try {
      const response = await fetch('/api/admin/popup/current');
      if (response.ok) {
        const data = await response.json();
        setPopupData(data);
      }
    } catch (error) {
      console.error('팝업 정보를 불러오는데 실패했습니다:', error);
      alert('팝업 정보를 불러오는데 실패했습니다.');
    }
  };

  // 파일 선택 처리
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // 파일 크기 체크 (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('파일 크기가 10MB를 초과합니다. 더 작은 파일을 선택해주세요.');
        fileInputRef.current.value = '';
        setSelectedFile(null);
        return;
      }

      // 이미지 파일 타입 체크
      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드 가능합니다.');
        fileInputRef.current.value = '';
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
    }
  };

  // 새 팝업 등록 (기존 팝업이 있으면 자동으로 삭제 후 등록)
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!selectedFile) {
      alert('업로드할 이미지를 선택해주세요.');
      return;
    }

    const confirmMessage = popupData.pu_no 
      ? '기존 팝업을 삭제하고 새 팝업을 등록하시겠습니까?'
      : '새 팝업을 등록하시겠습니까?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch('/api/admin/popup', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        alert('팝업이 성공적으로 등록되었습니다.');
        // 등록 후 현재 팝업 정보 다시 로드
        await loadCurrentPopup();
        // 파일 입력 초기화
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        alert('팝업 등록에 실패했습니다: ' + (result.message || '알 수 없는 오류'));
      }
    } catch (error) {
      console.error('팝업 등록 중 오류:', error);
      alert('팝업 등록 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 팝업 삭제
  const removePopup = async () => {
    if (!popupData.pu_no) {
      alert('삭제할 팝업이 없습니다.');
      return;
    }

    if (!window.confirm('현재 팝업을 삭제하시겠습니까?')) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/admin/popup', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pu_no: popupData.pu_no })
      });

      const result = await response.json();

      if (result.success) {
        alert('팝업이 성공적으로 삭제되었습니다.');
        await loadCurrentPopup();
      } else {
        alert('팝업 삭제에 실패했습니다: ' + (result.message || '알 수 없는 오류'));
      }
    } catch (error) {
      console.error('팝업 삭제 중 오류:', error);
      alert('팝업 삭제 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="wrap">
      <div className="over_hi" style={{ marginTop: '60px' }}>
        <div onSubmit={handleSubmit}>
          <table className="adminTable">
            <tbody>
              <tr>
                <th style={{ width: '200px' }}>현재 팝업 상태</th>
                <td className="left">
                  {popupData.pu_file1_s ? (
                    <div>
                      <div style={{ marginBottom: '10px' }}>
                        <span style={{ 
                          color: '#28a745', 
                          fontWeight: 'bold',
                          padding: '4px 8px',
                          backgroundColor: '#d4edda',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          ● 활성
                        </span>
                      </div>
                      <a href={`/resources/upload/${popupData.pu_file1_s}`} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={`/resources/upload/${popupData.pu_file1_s}`} 
                          alt="현재 팝업 이미지" 
                          style={{ maxWidth: '200px', border: '1px solid #ddd' }}
                        />
                      </a>
                    </div>
                  ) : (
                    <div>
                      <span style={{ 
                        color: '#dc3545', 
                        fontWeight: 'bold',
                        padding: '4px 8px',
                        backgroundColor: '#f8d7da',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        ● 비활성
                      </span>
                      <p style={{ marginTop: '10px', color: '#666' }}>
                        현재 등록된 팝업이 없습니다.
                      </p>
                    </div>
                  )}
                </td>
              </tr>
              
              <tr>
                <th style={{ width: '200px' }}>새 팝업 이미지</th>
                <td className="left">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={isLoading}
                  />
                  {selectedFile && (
                    <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                      선택된 파일: {selectedFile.name}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
          
          <div className="right">
            <button
              type="button"
              onClick={handleSubmit}
              className="common-del"
              disabled={isLoading}
            >
              {isLoading ? "처리중..." : popupData.pu_no ? "팝업 교체" : "팝업 등록"}
            </button> 
            {popupData.pu_no && (
              <button
                type="button"
                onClick={removePopup}
                className="common-del"
                disabled={isLoading}
              >
                {isLoading ? "처리중..." : "팝업 삭제"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}